/**
 * Validation shared by the editor form (client) and the repository/server.
 * Every article write is validated here, so an invalid slug, an over-long meta
 * description or a "scheduled" article with no date can never be persisted.
 */
import { z } from 'zod';
import { isValidSlug, MAX_SLUG_LENGTH } from './slug.js';
import { STATUSES } from './status.js';

const slugField = z
  .string()
  .min(1, 'Slug is required')
  .max(MAX_SLUG_LENGTH, `Slug must be ≤ ${MAX_SLUG_LENGTH} characters`)
  .refine(isValidSlug, 'Slug may only contain lowercase letters, numbers and single hyphens');

const optionalUrl = z
  .string()
  .trim()
  .refine((v) => v === '' || /^https?:\/\/\S+$/i.test(v), 'Must be a valid http(s) URL')
  .optional()
  .default('');

export const languageCodeField = z
  .string()
  .trim()
  .regex(/^[a-z]{2}(-[A-Z]{2})?$/, 'Use a BCP-47 code such as "en" or "te-IN"');

export const seoSchema = z.object({
  title: z.string().trim().max(70, 'SEO title should be ≤ 70 characters').optional().default(''),
  description: z.string().trim().max(160, 'Meta description should be ≤ 160 characters').optional().default(''),
  canonical: optionalUrl,
  ogImage: optionalUrl,
  noindex: z.boolean().optional().default(false),
});

export const articleSchema = z
  .object({
    title: z.string().trim().min(3, 'Title is too short').max(160),
    slug: slugField,
    dek: z.string().trim().max(320, 'Standfirst should be ≤ 320 characters').optional().default(''),
    body: z.string().min(1, 'Body cannot be empty'),
    coverImageUrl: optionalUrl,
    categoryId: z.string().min(1, 'Choose a category'),
    authorId: z.string().min(1, 'Choose an author'),
    languageCode: languageCodeField.default('en'),
    regionCode: z.string().trim().max(12).optional().default(''),
    tags: z.array(z.string().trim().min(1).max(40)).max(20).optional().default([]),
    status: z.enum(STATUSES),
    scheduledFor: z.string().datetime({ offset: true }).nullable().optional().default(null),
    seo: seoSchema.optional().default({}),
  })
  .superRefine((val, ctx) => {
    if (val.status === 'scheduled' && !val.scheduledFor) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['scheduledFor'], message: 'A scheduled article needs a publish date/time' });
    }
    if (val.status === 'scheduled' && val.scheduledFor && new Date(val.scheduledFor).getTime() <= Date.now()) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['scheduledFor'], message: 'Scheduled time must be in the future' });
    }
    if (val.seo && !val.seo.title) {
      // SEO title is optional, but if omitted it falls back to the headline — no warning needed.
    }
    if (val.seo && val.seo.noindex && val.status === 'published') {
      // Allowed, but intentional.
    }
  });

export const categorySchema = z.object({
  name: z.string().trim().min(2).max(60),
  slug: slugField,
  description: z.string().trim().max(200).optional().default(''),
  sortOrder: z.coerce.number().int().min(0).max(999).optional().default(0),
});

export const mediaSchema = z.object({
  fileName: z.string().trim().min(1).max(200),
  url: z.string().url(),
  mimeType: z.string().trim().min(3),
  sizeBytes: z.number().int().nonnegative(),
  altText: z.string().trim().max(250).optional().default(''),
  credit: z.string().trim().max(160).optional().default(''),
});

/** @returns {{ok:true, data:any}|{ok:false, errors:Record<string,string>}} */
export function validate(schema, value) {
  const res = schema.safeParse(value);
  if (res.success) return { ok: true, data: res.data };
  /** @type {Record<string,string>} */
  const errors = {};
  for (const issue of res.error.issues) {
    const key = issue.path.join('.') || '_';
    if (!errors[key]) errors[key] = issue.message;
  }
  return { ok: false, errors };
}
